/**
 * Artifact detail page (/artifact?id=…).
 *
 * Data comes from POST /artifacts/detail via the artifactDetail slice, mapped to
 * the ArtifactDetailView view model. Renders the artifact summary (headline
 * fields + the artifactDetails document), then the payments carried by the
 * artifact — found via SearchPayments (paymentIdentifiers=[artifactId]) and
 * shown as analytics + a table. The landing-page search bar is kept at the top
 * (without the "Beacon Lookup" title) so another lookup can be run without
 * navigating back.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Spinner from '@cloudscape-design/components/spinner';
import { DetailLink } from 'src/components/common/DetailLink';
import { useAppSelector, useAppDispatch } from 'src/store/hooks';
import {
  fetchArtifactDetail, clearArtifactDetail, selectArtifactRegion,
} from 'src/store/slices/artifactDetail/artifactDetailSlice';
import { searchPayments, clearSearchPayments } from 'src/store/slices/searchPayments/searchPaymentsSlice';
import { RegionResults } from 'src/components/common/RegionResults';
import { SearchTabs } from '../search/SearchTabs';
import { ArtifactSummary } from './summary/ArtifactSummary';
import { PaymentAnalytics } from '../payments/PaymentAnalytics';
import { PaymentDetailsTable } from '../payments/PaymentDetailsTable';

export const ArtifactDetailPage = () => {
  const [searchParams] = useSearchParams();
  const artifactId = searchParams.get('id') ?? '';
  const dispatch = useAppDispatch();
  const { region, global } = useAppSelector(state => state.env);
  const {
    data: detail, otherArtifactIds, loading, error, notFound,
    hits, activeRegion, searchedRegions, regionErrors,
  } = useAppSelector(state => state.artifactDetail);

  /** Wraps the body in a tab per region holding this artifact. */
  const byRegion = (body?: JSX.Element) => (
    <RegionResults
      entity="artifact"
      hits={hits}
      activeRegion={activeRegion}
      searchedRegions={searchedRegions}
      regionErrors={regionErrors}
      onSelectRegion={region => dispatch(selectArtifactRegion(region))}
    >
      {body}
    </RegionResults>
  );

  const load = () => {
    if (!artifactId) return undefined;
    return dispatch(fetchArtifactDetail(artifactId));
  };

  // Load whenever the id changes; abort any in-flight request.
  useEffect(() => {
    const promise = load();
    return () => {
      promise?.abort();
      dispatch(clearArtifactDetail());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifactId, dispatch]);

  // The payments this artifact carries live in the same region as the artifact,
  // so a global search has to wait for the lookup to land before it knows where
  // to ask. searchRegion is that region; in a single-region search it is just
  // the selected one and this runs immediately.
  const searchRegion = global ? activeRegion : region;
  useEffect(() => {
    if (!artifactId || !searchRegion) return undefined;
    const promise = dispatch(searchPayments({
      input: {
        filters: { paymentIdentifiers: [artifactId] },
        pagination: { pageNumber: 1, pageSize: 100 },
      },
      region: searchRegion,
    }));
    return () => {
      promise.abort();
      dispatch(clearSearchPayments());
    };
  }, [artifactId, searchRegion, dispatch]);

  const renderBody = () => {
    if (!artifactId) {
      return (
        <Alert type="warning" header="No artifact id">
          Provide an artifact id, for example /artifact?id=&lt;artifactId&gt;.
        </Alert>
      );
    }
    if (loading) {
      return (
        <Box textAlign="center" padding="xxl">
          <Spinner size="large" />
          <Box variant="p" color="text-body-secondary">Loading artifact {artifactId}…</Box>
        </Box>
      );
    }
    if (error) {
      return (
        <Alert type="error" header="Failed to load artifact" action={<Button onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      );
    }
    if (notFound || !detail) {
      return (
        <SpaceBetween size="l">
          <Alert type="warning" header="Artifact not found">
            No artifact matched <strong>{artifactId}</strong>
            {searchedRegions.length > 1 && ` in any of the ${searchedRegions.length} regions searched`}.
            GetArtifactDetail matches by artifactId; use artifact search to resolve other
            identifiers.
          </Alert>
          {byRegion()}
        </SpaceBetween>
      );
    }
    return byRegion(
      <SpaceBetween size="l">
        {otherArtifactIds.length > 0 && (
          <Alert type="info" header={`${otherArtifactIds.length} other artifact(s) matched this identifier`}>
            <SpaceBetween direction="horizontal" size="xs">
              {otherArtifactIds.map(otherId => (
                <DetailLink key={otherId} to={`/artifact?id=${encodeURIComponent(otherId)}`}>
                  {otherId}
                </DetailLink>
              ))}
            </SpaceBetween>
          </Alert>
        )}

        <ArtifactSummary />
        <PaymentAnalytics />
        <PaymentDetailsTable />
      </SpaceBetween>
    );
  };

  return (
    <Box padding={{ horizontal: 'l', top: 'l', bottom: 'xl' }}>
      <SpaceBetween size="l">
        <SearchTabs />
        {renderBody()}
      </SpaceBetween>
    </Box>
  );
};
